import { useEffect, useState } from "react";
import { firstContentPart, validCatalogSelection } from "../lib/catalog";
import { contentHash, routeHash } from "../lib/routing.mjs";
import type { Selection } from "../types";
import { BottomNavigation, Sidebar, Topbar, buildNav } from "./Navigation";
import { DetailDialog } from "./DetailDialog";
import { PersistentPlayer } from "./PersistentPlayer";
import { LanguageCtx, StoreContext, useLanguagePack, useRoute, useStore } from "./useStore";
import type { AppStore } from "./store";
import { AudioPage } from "../pages/AudioPage";
import { CatalogPage } from "../pages/CatalogPage";
import { LanguagePicker } from "../pages/LanguagePicker";
import { StatsPage } from "../pages/StatsPage";
import { StoriesPage } from "../pages/StoriesPage";
import { FlashcardsPage } from "../pages/FlashcardsPage";
import { StudyPage } from "../pages/StudyPage";

export function App({ store }: { store: AppStore }) {
  return (
    <StoreContext.Provider value={store}>
      <Shell />
    </StoreContext.Provider>
  );
}

/** Flashcards take over the viewport (no navigation or player chrome); deck browsing and speed rounds keep the normal layout. */
const isFocusStudy = (page: string, itemId?: string) => page === "study" && !["deck", "sprint"].includes(itemId ?? "");

function Shell() {
  const store = useStore();
  const route = useRoute(store);
  const languageId = route.page === "languages" ? null : route.languageId;
  const [attempt, setAttempt] = useState(0);
  const pack = useLanguagePack(store, languageId, attempt);
  const registered = store.registry.find((l) => l.id === languageId) ?? null;
  const name = pack.status === "ready" ? pack.pack.definition.name : registered?.name;
  const page = route.page;
  const playerVisible = !!store.playerState.program && !store.playerState.program.practiceToken;
  const itemId = route.page === "languages" ? undefined : route.itemId;
  const focusStudy = route.page !== "languages" && isFocusStudy(route.page, itemId);

  const ready = route.page !== "languages" && pack.status === "ready" ? pack.pack : null;
  // A content path with unknown ids falls back to its deepest valid level.
  const requested: Selection | null = route.page !== "languages" && route.trackId ? route : null;
  const valid = ready && requested ? validCatalogSelection(ready, requested) : null;
  const invalid = !!(valid && requested && (["trackId", "collectionId", "partId"] as const).some((k) => requested[k] !== valid[k]));
  const selection: Selection | null = !ready ? null
    : route.page !== "languages" && route.partId ? { trackId: route.trackId, collectionId: route.collectionId, partId: route.partId }
    : firstContentPart(ready);

  useEffect(() => {
    if (!ready || !invalid || !valid || route.page === "languages") return;
    location.hash = valid.trackId ? contentHash(route.languageId, valid, valid.partId ? "part" : "catalog") : routeHash(route.languageId);
  }, [ready, invalid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { document.body.classList.toggle("has-player", playerVisible && !focusStudy); }, [playerVisible, focusStudy]);
  useEffect(() => {
    document.body.classList.toggle("study-focus", focusStudy);
    if (focusStudy) store.player.stop();
    return () => document.body.classList.remove("study-focus");
  }, [focusStudy, store]);
  useEffect(() => {
    document.title = "Hibiki" + (name ? ` — ${name}` : " — Make room for a language");
  }, [name]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior }); }, [languageId, page, itemId]);

  let body;
  if (route.page === "languages") body = <LanguagePicker />;
  else if (pack.status === "loading") body = <p className="loading">Getting your practice ready…</p>;
  else if (pack.status === "error") {
    body = (
      <section className="empty-state">
        <h1>Your language couldn’t load.</h1>
        <p>Check your connection and try again. Your saved progress is still here.</p>
        <button className="button" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
        <a className="text-link" href="#/languages">Choose a language</a>
      </section>
    );
  }

  const ctx = ready && !invalid ? store.contextFor(ready, selection) : null;
  if (ctx && route.page !== "languages") {
    const key = `${route.languageId}/${selection?.partId ?? ""}/${route.page}/${route.itemId ?? ""}/${store.routeNonce}`;
    body = (
      <LanguageCtx.Provider value={ctx}>
        {(route.page === "home" || route.page === "catalog" || route.page === "part") && <CatalogPage key={key} route={{ ...route, page: route.page }} />}
        {route.page === "study" && (focusStudy ? <FlashcardsPage key={key} itemId={route.itemId} /> : <StudyPage key={key} itemId={route.itemId} />)}
        {route.page === "audio" && <AudioPage key={key} itemId={route.itemId} />}
        {route.page === "stories" && <StoriesPage key={key} itemId={route.itemId} />}
        {route.page === "stats" && <StatsPage key={key} />}
        <DetailDialog />
      </LanguageCtx.Provider>
    );
  }

  const nav = buildNav(route, ready, selection, registered?.name);
  return (
    <>
      <a className="skip-link" href="#main-content" onClick={(e) => { e.preventDefault(); document.getElementById("main-content")?.focus(); }}>Skip to content</a>
      <div className={"app-shell" + (languageId ? "" : " selecting-language")}>
        <Sidebar nav={nav} />
        <div className="main-column">
          <Topbar nav={nav} />
          {store.saveWarning && <p className="save-status" role="status">{store.saveWarning}</p>}
          <main id="main-content" tabIndex={-1}><div id="view">{body}</div></main>
        </div>
      </div>
      <BottomNavigation nav={nav} />
      <PersistentPlayer />
    </>
  );
}
